import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle,
  Circle,
  ExternalLink,
  MessageCircle,
  Shield,
  Phone,
  Key,
  Webhook,
  ArrowRight,
  UserCheck,
  Settings,
  ToggleRight,
  FileText,
  BadgeCheck,
  Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/portal/messaging/whatsapp-setup")({
  component: WhatsAppSetupPage,
});

interface SetupStep {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  details: React.ReactNode;
}

function WhatsAppSetupPage() {
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  const steps: SetupStep[] = [
    {
      id: "business-account",
      title: "Create a Meta Business Account",
      icon: Shield,
      description: "[Business Suite] Set up your Meta Business Account and verify your business",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            A verified Meta Business Account is required to use WhatsApp Business API.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to{" "}
              <a
                href="https://business.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline inline-flex items-center gap-1"
              >
                Meta Business Suite <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Click &quot;Create Account&quot; if you don&apos;t have one</li>
            <li>Complete business verification (requires business documents)</li>
            <li>Wait for Meta to verify your business (typically 2-5 business days)</li>
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            Account creation is instant. The lengthier <strong>Business Verification</strong>
            {" "}is its own step below — start it as early as possible.
          </div>
        </div>
      ),
    },
    {
      id: "business-verification",
      title: "Start Business Verification",
      icon: BadgeCheck,
      description: "[Business Suite] 2–5 day Meta review — start this NOW, runs in the background",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Business Verification is the single biggest blocker to going live. Meta
            locks the &quot;Live&quot; app mode toggle until verification completes,
            and the review takes 2–5 business days. Start it immediately so it
            runs while you finish the rest of setup.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to{" "}
              <a
                href="https://business.facebook.com/settings/security"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline inline-flex items-center gap-1"
              >
                Meta Business Suite &rarr; Security Center <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Click &quot;Start Verification&quot;</li>
            <li>Upload required documents (varies by country — typically):
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li>Business registration certificate (CIPC for SA)</li>
                <li>Proof of business address (utility bill, lease)</li>
                <li>Business phone number that matches the registration</li>
              </ul>
            </li>
            <li>Submit and wait — typically 2–5 business days</li>
          </ol>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            <strong>Until this completes:</strong> the app stays in Development mode
            and can only send to allowlisted test numbers. Going Live (Step 10) is
            blocked.
          </div>
        </div>
      ),
    },
    {
      id: "whatsapp-app",
      title: "Create a WhatsApp Business App",
      icon: MessageCircle,
      description: "[Developer Console] Register your app at developers.facebook.com",
      details: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to{" "}
              <a
                href="https://developers.facebook.com/apps"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline inline-flex items-center gap-1"
              >
                Meta Developers <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>Click &quot;Create App&quot; &rarr; select &quot;Business&quot; type</li>
            <li>Link it to your Meta Business Account</li>
            <li>On the app dashboard, find &quot;WhatsApp&quot; and click &quot;Set Up&quot;</li>
          </ol>
        </div>
      ),
    },
    {
      id: "phone-number",
      title: "Add & Verify a Phone Number (Cloud API)",
      icon: Phone,
      description: "[developers.facebook.com → App → WhatsApp → API Setup] Register the number for Cloud API and verify ownership",
      details: (
        <div className="space-y-3">
          <div className="rounded-lg bg-red-50 border border-red-300 p-3 text-sm text-red-900">
            <strong>⚠️ Read this BEFORE you click anything.</strong>
            <p className="mt-1">
              The phone number you want to use cannot also be in use on the
              regular <strong>WhatsApp</strong> app or <strong>WhatsApp Business</strong>{" "}
              app. The moment you verify it here, Meta deactivates that account
              and <strong>all device-side chat history for that number is lost</strong>{" "}
              (no recovery).
            </p>
            <p className="mt-2">
              <strong>Confirm with the number&apos;s owner first.</strong> If
              they want to preserve chats, they must either export them from
              the phone OR open a Meta support ticket asking Meta to migrate
              the number for them (preserves chats, takes 1–2 business days).
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            This must happen in the <strong>developer console</strong> at{" "}
            <a
              href="https://developers.facebook.com/apps/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline inline-flex items-center gap-1"
            >
              developers.facebook.com <ExternalLink className="h-3 w-3" />
            </a>
            , NOT in Business Suite&apos;s WhatsApp Manager. The Business Suite
            path creates legacy <code className="text-xs bg-white px-1 rounded">ON_PREMISE</code>{" "}
            registrations that do not work with our Cloud API integration —
            you will get a verified-but-disconnected number and have to
            redo this whole step.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to <strong>developers.facebook.com → My Apps → [your app]</strong>
            </li>
            <li>
              Left sidebar: <strong>WhatsApp → API Setup</strong> (sometimes
              labelled <em>Quickstart</em>)
            </li>
            <li>
              In the <strong>&quot;From&quot;</strong> dropdown click{" "}
              <strong>&quot;Manage phone number list&quot;</strong> →{" "}
              <strong>&quot;Add phone number&quot;</strong>
            </li>
            <li>Enter the business phone number → Next</li>
            <li>
              Pick a <strong>6-digit PIN</strong> (note it down — you&apos;ll
              need it again if Meta ever de-registers the number)
            </li>
            <li>
              Choose <strong>SMS</strong> or <strong>Voice call</strong> to
              receive the verification code on that phone → enter the code
            </li>
            <li>
              The phone is now <strong>verified + registered as Cloud API</strong>.
              The &quot;From&quot; dropdown now lists it with its Phone Number
              ID right next to it.
            </li>
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
            <strong>What you need to copy from this screen:</strong>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>
                <strong>Phone Number ID</strong> — the long numeric string shown
                next to the phone in the &quot;From&quot; dropdown (e.g.{" "}
                <code className="text-xs bg-white px-1 rounded">1136047482915309</code>).
              </li>
              <li>
                <strong>WhatsApp Business Account ID (WABA ID)</strong> — shown
                at the top of the same API Setup page.
              </li>
            </ul>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>If the &quot;Add phone number&quot; dialog says &quot;This phone
            number is already registered to a WhatsApp account&quot;:</strong> the
            number is still attached to consumer WhatsApp / Business app
            somewhere. Open that app on the device using the number → Settings
            → Account → <strong>Delete my account</strong> → confirm. Wait ~3
            minutes, then click <em>Try again</em> in Meta&apos;s dialog. This
            is destructive (loses chat history); see the red warning above.
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Display Name approval:</strong> The display name shown to
            recipients is approved by Meta (usually instant, can take up to 24h).
            Without an approved display name the number can only send via
            templates.
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
            <strong>Sanity check before moving on:</strong> back in API Setup,
            the phone should show status{" "}
            <code className="text-xs bg-white px-1 rounded">CONNECTED</code>{" "}
            and <code className="text-xs bg-white px-1 rounded">platform_type: CLOUD_API</code>.
            If it shows <code className="text-xs bg-white px-1 rounded">DISCONNECTED</code>{" "}
            or <code className="text-xs bg-white px-1 rounded">ON_PREMISE</code>,
            do NOT continue — the rest of the integration will fail. Re-do this
            step via API Setup, not WhatsApp Manager.
          </div>
        </div>
      ),
    },
    {
      id: "access-token",
      title: "Generate a Permanent Access Token",
      icon: Key,
      description: "[Business Suite] Create a System User token for API access",
      details: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Go to Meta Business Suite &rarr; Settings &rarr; Business Users</li>
            <li>Create a &quot;System User&quot; (or use an existing one)</li>
            <li>Assign the WhatsApp Business app with these permissions:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><code className="text-xs bg-muted px-1 rounded">whatsapp_business_messaging</code></li>
                <li><code className="text-xs bg-muted px-1 rounded">whatsapp_business_management</code></li>
              </ul>
            </li>
            <li>Generate a <strong>permanent</strong> token (NOT the temporary 24-hour token)</li>
          </ol>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            <strong>Security:</strong> Never share your access token. Store it securely
            and only enter it in the channel configuration form (Step 6).
          </div>
        </div>
      ),
    },
    {
      id: "test-recipients",
      title: "Add Test Recipients in Meta",
      icon: UserCheck,
      description: "[Developer Console] Allowlist phone numbers you can send test messages to",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Until your business is verified by Meta, you can only send messages to
            phone numbers explicitly added to your test recipient list.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>In Meta Developer Console &rarr; your WhatsApp app &rarr; API Setup</li>
            <li>Find the &quot;To&quot; section and click &quot;Manage phone number list&quot;</li>
            <li>Add the WhatsApp numbers you want to send test messages to</li>
            <li>Each recipient must confirm via a code sent to their WhatsApp</li>
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Important:</strong> Skip this step at your peril — sends to numbers
            not on the list will silently fail with a permissions error until business
            verification is complete.
          </div>
        </div>
      ),
    },
    {
      id: "configure-channel",
      title: "Configure the Channel in Larasoft",
      icon: Settings,
      description: "[Larasoft Portal] Enter your Meta credentials — we auto-register the phone number",
      details: (
        <div className="space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to{" "}
              <Link
                to="/portal/messaging"
                className="text-primary underline"
              >
                Channels
              </Link>
              {" "}and click &quot;Add Channel&quot;
            </li>
            <li>Select &quot;WhatsApp&quot; as the channel type</li>
            <li>Enter a display name (e.g. &quot;WhatsApp Business&quot;)</li>
            <li>Enter the credentials you collected in earlier steps:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><strong>Phone Number ID</strong> (from Step 3)</li>
                <li><strong>WhatsApp Business Account ID</strong> (from Step 3)</li>
                <li><strong>Access Token</strong> (from Step 4)</li>
              </ul>
            </li>
            <li>Click &quot;Add Channel&quot; to save (channel starts as Inactive)</li>
            <li>
              A &quot;Webhook Settings&quot; dialog will pop up automatically with the{" "}
              <strong>Callback URL</strong> and an auto-generated{" "}
              <strong>Verify Token</strong>. Leave it open — you&apos;ll copy both
              into Meta in the next step.
            </li>
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
            <strong>Closed the dialog?</strong> No problem — open it again from the
            channel row menu &rarr; &quot;Webhook Settings&quot;.
          </div>
        </div>
      ),
    },
    {
      id: "configure-webhook",
      title: "Configure Webhook in Meta",
      icon: Webhook,
      description: "[Developer Console] Point Meta at our endpoint so we receive inbound messages",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Without this step, inbound WhatsApp messages will never reach Larasoft.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Go to your WhatsApp app in Meta Developer Console</li>
            <li>Navigate to <strong>Configuration &rarr; Webhook</strong></li>
            <li>Click &quot;Edit&quot; and paste in the values from the Webhook
              Settings dialog (Step 6):
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><strong>Callback URL</strong> — use the Copy button in Larasoft</li>
                <li><strong>Verify Token</strong> — use the Copy button in Larasoft</li>
              </ul>
            </li>
            <li>Click &quot;Verify and Save&quot; — Meta will call our endpoint immediately to confirm</li>
            <li>
              Under &quot;Webhook fields&quot;, click &quot;Manage&quot; and subscribe to:
              <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                <li><code className="text-xs bg-muted px-1 rounded">messages</code> (required — inbound messages and delivery statuses)</li>
              </ul>
            </li>
          </ol>
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            <strong>If verification fails:</strong> Double-check that you used the
            Copy buttons (no leading/trailing whitespace) and that the channel was
            saved in Step 6 before retrying.
          </div>
        </div>
      ),
    },
    {
      id: "activate-test",
      title: "Send a Test Message",
      icon: ToggleRight,
      description: "[Larasoft Portal] Verify end-to-end send/receive (channel auto-activates after webhook verify)",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The channel auto-activates the moment Meta successfully verifies our
            webhook in Step 7 — no manual toggle needed. Check the{" "}
            <Link to="/portal/messaging" className="text-primary underline">
              Channels
            </Link>{" "}
            page; status should show <strong>Active</strong>.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              <strong>Inbound test:</strong> Send a WhatsApp message FROM one of your
              test recipient numbers (Step 5) TO your registered business number. It
              should appear in the Larasoft inbox within seconds.
            </li>
            <li>
              <strong>Outbound test:</strong> Reply to that thread from Larasoft. The
              reply should arrive on the recipient&apos;s WhatsApp.
            </li>
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
            <strong>24-hour service window:</strong> You can send freeform replies for
            24 hours after a customer messages you. Outside that window, only approved
            templates can be sent.
          </div>
        </div>
      ),
    },
    {
      id: "go-live",
      title: "Flip App from Development to Live",
      icon: Rocket,
      description: "[Developer Console] Remove the test-recipient allowlist (requires verified business)",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            While the app is in Development mode, you can only message phone numbers
            on the allowlist (Step 6). Flipping to Live removes that restriction.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Confirm <strong>Business Verification</strong> (Step 2) has completed —
              the toggle is locked until then
            </li>
            <li>Confirm the Display Name on your phone number is <strong>Approved</strong></li>
            <li>Accept the WhatsApp Business Platform Terms (prompt appears in the WhatsApp product config after verification clears)</li>
            <li>
              In Meta Developer Console &rarr; your app &rarr; top of the page, toggle{" "}
              <strong>App Mode: Development &rarr; Live</strong>
            </li>
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
            <strong>Messaging tiers:</strong> New Live numbers start at 250 unique
            customer conversations / 24h. The tier auto-escalates (1K &rarr; 10K
            &rarr; 100K &rarr; unlimited) based on send volume and message quality
            rating over a rolling 7-day window.
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>App Review is NOT required</strong> for WhatsApp Business API
            (unlike Facebook Login or Instagram APIs). Business Verification + Live
            toggle is enough.
          </div>
        </div>
      ),
    },
    {
      id: "templates",
      title: "Submit Message Templates (for outbound campaigns)",
      icon: FileText,
      description: "[Business Suite + Larasoft Portal] Required for proactive messages outside the 24h window",
      details: (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Skip this step if you only need to reply to inbound customer messages.
            Required if you want to send notifications, reminders, or marketing
            messages.
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>
              Go to{" "}
              <Link to="/portal/messaging/templates" className="text-primary underline">
                Templates
              </Link>
              {" "}in Larasoft and click &quot;New Template&quot;
            </li>
            <li>Draft your template content with placeholder variables</li>
            <li>Click &quot;Submit for Approval&quot; — we forward it to Meta</li>
            <li>
              Wait for Meta approval (typically minutes to hours, occasionally up to
              24h). The template status will update automatically when Meta responds.
            </li>
            <li>Once <strong>Approved</strong>, the template can be used for outbound sends</li>
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Meta&apos;s content rules are strict:</strong> Promotional content
            and overly generic wording is frequently rejected. Use clear,
            transactional language for your first templates.
          </div>
        </div>
      ),
    },
  ];

  const completedCount = completedSteps.size;
  const totalSteps = steps.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-slate-900">WhatsApp Setup</h1>
        <p className="mt-2 text-lg text-slate-600">
          Step-by-step guide to connect WhatsApp Business to your organisation
        </p>
      </div>

      {/* Where you'll be working — the two Meta portals + Larasoft */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Where you'll be working</CardTitle>
          <CardDescription>
            WhatsApp setup spans three places. Each step below is tagged with the
            portal you'll be in — read this once so you don't go hunting in the
            wrong tool.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-primary" />
                <h4 className="font-semibold text-slate-900">Meta Business Suite</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                <a
                  href="https://business.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline break-all"
                >
                  business.facebook.com
                </a>
              </p>
              <p className="text-xs text-slate-600">
                Business account, business verification, WhatsApp phone numbers,
                display names, system users + permanent tokens, message templates.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="h-4 w-4 text-primary" />
                <h4 className="font-semibold text-slate-900">Meta Developer Console</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                <a
                  href="https://developers.facebook.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline break-all"
                >
                  developers.facebook.com
                </a>
              </p>
              <p className="text-xs text-slate-600">
                App registration, webhook configuration (callback URL + verify
                token), test recipient allowlist, flipping the app from
                Development to Live.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="h-4 w-4 text-primary" />
                <h4 className="font-semibold text-slate-900">Larasoft Portal</h4>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                <Link to="/portal/messaging" className="text-primary underline">
                  /portal/messaging
                </Link>
              </p>
              <p className="text-xs text-slate-600">
                Channel configuration (paste the credentials you collected from
                Meta), webhook settings dialog (auto-generated verify token to
                paste back into Meta), inbox + send.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Setup Progress</CardTitle>
          <CardDescription>
            {completedCount === totalSteps
              ? "All steps completed! Your WhatsApp channel is ready."
              : `${completedCount} of ${totalSteps} steps completed`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  completedSteps.has(step.id)
                    ? "bg-green-500"
                    : "bg-slate-200"
                }`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <Accordion type="single" collapsible className="space-y-2">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.has(step.id);
            const StepIcon = step.icon;

            return (
              <AccordionItem key={step.id} value={step.id} className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStep(step.id);
                      }}
                      className="shrink-0"
                    >
                      {isCompleted ? (
                        <CheckCircle className="h-6 w-6 text-green-500" />
                      ) : (
                        <Circle className="h-6 w-6 text-slate-300" />
                      )}
                    </button>
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <StepIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          Step {index + 1}
                        </span>
                        {isCompleted && (
                          <Badge variant="success" className="text-xs">
                            Done
                          </Badge>
                        )}
                      </div>
                      <div className="font-medium">{step.title}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-[4.5rem] pb-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    {step.description}
                  </p>
                  {step.details}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {/* Next Steps */}
      {completedCount === totalSteps && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-8 w-8 text-green-500 shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-green-900 mb-1">
                  WhatsApp is ready!
                </h3>
                <p className="text-sm text-green-800 mb-3">
                  Your WhatsApp Business channel is configured. You can now:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/portal/messaging">
                    <Button variant="outline" size="sm">
                      View Channels <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                  <Link to="/portal/messaging/templates">
                    <Button variant="outline" size="sm">
                      Manage Templates <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
